/**
 * features/finance/supplierWrite.ts
 * ------------------------------------------------------------------
 * Recording who the school pays, and into which account.
 *
 * The arithmetic and the rules live in lib/suppliers, which is pure and
 * tested. This is the part that touches Firestore.
 *
 * BANKING IS A SEPARATE ACT FROM EVERYTHING ELSE, and deliberately so.
 * Changing a phone number and changing where the money goes are not the same
 * kind of decision, and a single "save" that quietly does both is how a
 * supplier's account number gets edited by somebody who thought they were
 * updating a contact. So `saveSupplier` cannot touch banking at all;
 * `changeBankDetails` is its own call, takes a reason, and audits under its
 * own action name.
 *
 * VERIFICATION IS NOT SELF-SERVICE. `verifyBankDetails` records that a
 * person confirmed the digits against something other than the message that
 * asked for the change, and it insists on being told HOW. "Verified" with no
 * method behind it is a tick somebody clicked.
 * ------------------------------------------------------------------ */
import { doc, getDoc } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import {
  SUPPLIER_COLLECTION, supplierKey, supplierDocId, supplierProblem, bankProblem,
  applyBankChange, bankDetailsDiffer,
  type Supplier, type SupplierBank,
} from '@/lib/suppliers';

export interface Actor {
  uid?: string;
  email?: string;
  displayName?: string;
  role?: string;
}

const nowIso = () => new Date().toISOString();

/**
 * Create or update a supplier's identity and contact details.
 *
 * NEVER TOUCHES BANKING. If a caller passes bank fields they are ignored —
 * silently accepting them here would make the separate, audited banking path
 * optional, and optional controls are the ones that are not there when they
 * matter.
 */
export async function saveSupplier(
  input: Partial<Supplier> & { name: string },
  actor: Actor,
): Promise<Supplier> {
  const problem = supplierProblem(input);
  if (problem) throw new Error(problem);

  const name = input.name.trim();
  const id = input.id ?? supplierDocId(name);
  const ref = doc(db, SUPPLIER_COLLECTION, id);
  const existing = await getDoc(ref);
  const prior = existing.exists() ? (existing.data() as Supplier) : undefined;

  const record: Supplier = {
    id,
    name,
    nameKey: supplierKey(name),
    ...(input.contactPerson !== undefined ? { contactPerson: input.contactPerson.trim() } : {}),
    ...(input.contactPhone !== undefined ? { contactPhone: input.contactPhone.trim() } : {}),
    ...(input.contactEmail !== undefined ? { contactEmail: input.contactEmail.trim() } : {}),
    ...(input.address !== undefined ? { address: input.address.trim() } : {}),
    ...(input.tin !== undefined ? { tin: input.tin.trim() } : {}),
    ...(input.vatNumber !== undefined ? { vatNumber: input.vatNumber.trim() } : {}),
    ...(input.notes !== undefined ? { notes: input.notes.trim() } : {}),
    active: input.active ?? prior?.active ?? true,
    // Carried through untouched. This function does not decide banking.
    ...(prior?.bank ? { bank: prior.bank } : {}),
    ...(prior?.bankVerifiedAt ? { bankVerifiedAt: prior.bankVerifiedAt } : {}),
    ...(prior?.bankVerifiedBy ? { bankVerifiedBy: prior.bankVerifiedBy } : {}),
    ...(prior?.bankVerifiedByName ? { bankVerifiedByName: prior.bankVerifiedByName } : {}),
    ...(prior?.bankVerifiedHow ? { bankVerifiedHow: prior.bankVerifiedHow } : {}),
    ...(prior?.bankHistory ? { bankHistory: prior.bankHistory } : {}),
    createdAt: prior?.createdAt ?? nowIso(),
    createdBy: prior?.createdBy ?? actor.uid,
    updatedAt: nowIso(),
    updatedBy: actor.uid,
  };

  await setDoc(ref, record);
  auditFromContext(actor as never, prior ? 'supplier_updated' : 'supplier_created', 'finance', {
    targetId: id,
    targetType: 'setting',
    targetName: name,
    details: { contactEmail: record.contactEmail ?? null, active: record.active },
  });
  return record;
}

/**
 * Change where a supplier is paid.
 *
 * A REASON IS REQUIRED. The realistic fraud is an email on convincing
 * letterhead saying the banking has changed; making somebody type why
 * creates the one record that later says whether anybody asked. It costs a
 * sentence and it is the only part of this that survives into an
 * investigation.
 *
 * The verification is cleared by applyBankChange, in the pure module, so a
 * future caller cannot forget to do it.
 */
export async function changeBankDetails(
  supplierId: string,
  next: SupplierBank,
  reason: string,
  actor: Actor,
): Promise<Supplier> {
  const problem = bankProblem(next);
  if (problem) throw new Error(problem);
  if (String(reason ?? '').trim().length < 4) {
    throw new Error('Say why the banking is changing. It is the record that matters afterwards.');
  }

  const ref = doc(db, SUPPLIER_COLLECTION, supplierId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That supplier no longer exists.');
  const current = snap.data() as Supplier;

  const changed = bankDetailsDiffer(current.bank, next);
  const updated = applyBankChange(
    current,
    next,
    { uid: actor.uid, name: actor.displayName ?? actor.email },
    reason.trim(),
  );

  await setDoc(ref, updated);
  auditFromContext(actor as never, 'supplier_bank_changed', 'finance', {
    targetId: supplierId,
    targetType: 'setting',
    targetName: current.name,
    details: {
      // The old number is recorded in the audit as well as on the supplier:
      // an investigation should not depend on one document surviving.
      from: current.bank?.accountNumber ?? null,
      to: next.accountNumber ?? null,
      bank: next.bankName ?? null,
      reason: reason.trim(),
      verificationCleared: changed,
    },
  });
  return updated;
}

/**
 * Record that somebody confirmed the banking.
 *
 * HOW is required. "Verified" with nothing behind it is a tick, and a tick
 * is exactly what an attacker is hoping the control amounts to.
 */
export async function verifyBankDetails(
  supplierId: string,
  how: string,
  actor: Actor,
): Promise<void> {
  if (String(how ?? '').trim().length < 4) {
    throw new Error(
      'Say how it was confirmed — a phone call to the number on the contract, a '
      + 'cancelled cheque, a letter collected in person.',
    );
  }
  const ref = doc(db, SUPPLIER_COLLECTION, supplierId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('That supplier no longer exists.');
  const s = snap.data() as Supplier;
  if (!s.bank?.accountNumber) {
    throw new Error('There are no bank details on this supplier to confirm.');
  }

  await updateDoc(ref, {
    bankVerifiedAt: nowIso(),
    bankVerifiedBy: actor.uid ?? '',
    bankVerifiedByName: actor.displayName ?? actor.email ?? '',
    bankVerifiedHow: how.trim(),
    updatedAt: nowIso(),
    updatedBy: actor.uid ?? '',
  });
  auditFromContext(actor as never, 'supplier_bank_verified', 'finance', {
    targetId: supplierId,
    targetType: 'setting',
    targetName: s.name,
    details: { how: how.trim(), account: s.bank.accountNumber },
  });
}

/**
 * Stop using a supplier. Never delete one.
 *
 * Vouchers, payments and a year of ledger entries refer to them. A supplier
 * that vanishes leaves a payment history pointing at nothing, which is worse
 * than a list with some inactive names in it.
 */
export async function setSupplierActive(
  supplierId: string,
  active: boolean,
  actor: Actor,
): Promise<void> {
  await updateDoc(doc(db, SUPPLIER_COLLECTION, supplierId), {
    active,
    updatedAt: nowIso(),
    updatedBy: actor.uid ?? '',
  });
  auditFromContext(actor as never, active ? 'supplier_reactivated' : 'supplier_deactivated', 'finance', {
    targetId: supplierId,
    targetType: 'setting',
  });
}
